import { useMemo } from "react";
import {
	Box,
	Paper,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	TrendingDownRounded,
	TrendingFlatRounded,
	TrendingUpRounded,
} from "@mui/icons-material";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip as TooltipGrafico,
	XAxis,
	YAxis,
} from "recharts";
import { useCentralAlertas } from "../context";
import { formatarDuracaoMinutos } from "../../../utils/centralDeAlertas/labels";
import useStyles from "./styles";
import { GUIDES_CENTRAL, MetricInfo } from "../metricInfo";

// Bloco "SLA por cliente" da Central de Alertas: barras empilhadas
// horizontais (cumprido vs estourado) + tabela resumo por cliente.
// A ordem de slaClientes já vem do context (pior SLA primeiro).

const ALTURA_LINHA_GRAFICO = 40;
const ALTURA_MINIMA_GRAFICO = 180;
const LIMITE_NOME_EIXO = 18;

const ICONES_TENDENCIA = {
	melhora: TrendingUpRounded,
	piora: TrendingDownRounded,
	estavel: TrendingFlatRounded,
};

const TITULOS_TENDENCIA = {
	melhora: "Melhor que o período anterior",
	piora: "Pior que o período anterior",
	estavel: "Estável vs período anterior",
};

function formatarPct(pct) {
	if (pct === null || pct === undefined) return "—";
	return `${Math.round(pct)}%`;
}

function truncarNome(nome) {
	if (!nome) return "—";
	if (nome.length <= LIMITE_NOME_EIXO) return nome;
	return `${nome.slice(0, LIMITE_NOME_EIXO - 1)}…`;
}

const SlaPorCliente = () => {
	const theme = useTheme();
	const styles = useStyles(theme);
	const { carregando, slaClientes } = useCentralAlertas();

	// Achata as linhas para o formato do recharts, preservando a ordem.
	const dadosGrafico = useMemo(
		() =>
			(slaClientes || []).map((linha) => ({
				nome: linha.cliente?.nome || "—",
				slaCumpridos: linha.slaCumpridos,
				slaEstourados: linha.slaEstourados,
				pctCumprido: linha.pctCumprido,
			})),
		[slaClientes]
	);

	const alturaGrafico = Math.max(
		ALTURA_MINIMA_GRAFICO,
		dadosGrafico.length * ALTURA_LINHA_GRAFICO
	);

	const renderTooltipGrafico = ({ active, payload }) => {
		if (!active || !payload || !payload.length) return null;
		const linha = payload[0].payload;
		return (
			<Box sx={styles.tooltipGrafico}>
				<Typography sx={styles.tooltipTitulo}>{linha.nome}</Typography>
				<Typography sx={styles.tooltipLinha}>
					{`SLA cumprido: ${linha.slaCumpridos}`}
				</Typography>
				<Typography sx={styles.tooltipLinha}>
					{`SLA estourado: ${linha.slaEstourados}`}
				</Typography>
				<Typography sx={styles.tooltipLinha}>
					{`% cumprido: ${formatarPct(linha.pctCumprido)}`}
				</Typography>
			</Box>
		);
	};

	const cabecalho = (
		<Box sx={styles.linhaCabecalho}>
			<Box sx={styles.cabecalho}>
				<Typography variant="mySubtitle">SLA por cliente</Typography>
				<Typography variant="caption" sx={styles.caption}>
					Cumprido vs estourado no período
				</Typography>
			</Box>
			<MetricInfo theme={theme} guide={GUIDES_CENTRAL.slaPorCliente} />
		</Box>
	);

	if (carregando) {
		return (
			<Paper sx={styles.container}>
				{cabecalho}
				<Skeleton
					variant="rounded"
					height={ALTURA_MINIMA_GRAFICO}
					sx={styles.skeletonGrafico}
				/>
				<Box sx={styles.boxSkeletonLinhas}>
					<Skeleton variant="text" />
					<Skeleton variant="text" />
					<Skeleton variant="text" />
				</Box>
			</Paper>
		);
	}

	if (!dadosGrafico.length) {
		return (
			<Paper sx={styles.container}>
				{cabecalho}
				<Box sx={styles.estadoVazio}>
					<Typography color="text.secondary">
						Nenhum incidente no período selecionado.
					</Typography>
				</Box>
			</Paper>
		);
	}

	return (
		<Paper sx={styles.container}>
			{cabecalho}
			<Box sx={styles.areaGrafico(alturaGrafico)}>
				<ResponsiveContainer width="100%" height="100%">
					<BarChart
						data={dadosGrafico}
						layout="vertical"
						margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
					>
						<CartesianGrid
							horizontal={false}
							stroke={styles.cores.divisor}
							strokeDasharray="3 3"
						/>
						<XAxis
							type="number"
							allowDecimals={false}
							tick={styles.tickEixo}
							tickLine={false}
							axisLine={{ stroke: styles.cores.divisor }}
						/>
						<YAxis
							type="category"
							dataKey="nome"
							width={140}
							tick={styles.tickEixo}
							tickLine={false}
							axisLine={false}
							tickFormatter={truncarNome}
						/>
						<TooltipGrafico
							content={renderTooltipGrafico}
							cursor={styles.cursorGrafico}
						/>
						<Legend
							iconSize={10}
							formatter={(valor) => (
								<span style={styles.legendaTexto}>{valor}</span>
							)}
						/>
						<Bar
							dataKey="slaCumpridos"
							name="SLA cumprido"
							stackId="sla"
							barSize={20}
							fill={styles.cores.cumprido}
							stroke={styles.cores.papel}
							strokeWidth={1}
						/>
						<Bar
							dataKey="slaEstourados"
							name="SLA estourado"
							stackId="sla"
							barSize={20}
							fill={styles.cores.estourado}
							stroke={styles.cores.papel}
							strokeWidth={1}
							radius={[0, 3, 3, 0]}
						/>
					</BarChart>
				</ResponsiveContainer>
			</Box>
			<TableContainer sx={styles.tabelaContainer}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={styles.celulaCabecalho}>Cliente</TableCell>
							<TableCell sx={styles.celulaCabecalho} align="right">
								Contrato (min)
							</TableCell>
							<TableCell sx={styles.celulaCabecalho} align="right">
								Incidentes
							</TableCell>
							<TableCell sx={styles.celulaCabecalho} align="right">
								SLA cumprido (%)
							</TableCell>
							<TableCell sx={styles.celulaCabecalho} align="right">
								MTTR médio
							</TableCell>
							<TableCell sx={styles.celulaCabecalho} align="center">
								Tendência
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{slaClientes.map((linha) => {
							const IconeTendencia =
								ICONES_TENDENCIA[linha.tendencia] || TrendingFlatRounded;
							return (
								<TableRow key={linha.cliente?.id || linha.cliente?.nome}>
									<TableCell sx={styles.celulaCliente}>
										{linha.cliente?.nome || "—"}
									</TableCell>
									<TableCell align="right">
										{linha.contratoSla ?? "—"}
									</TableCell>
									<TableCell align="right">{linha.totalIncidentes}</TableCell>
									<TableCell
										align="right"
										sx={
											linha.pctCumprido === null
												? undefined
												: styles.celulaPct(linha.pctCumprido)
										}
									>
										{formatarPct(linha.pctCumprido)}
									</TableCell>
									<TableCell align="right">
										{formatarDuracaoMinutos(linha.mttrMedio)}
									</TableCell>
									<TableCell align="center">
										<Tooltip
											title={
												TITULOS_TENDENCIA[linha.tendencia] ||
												TITULOS_TENDENCIA.estavel
											}
											arrow
										>
											<IconeTendencia
												sx={styles.iconeTendencia(linha.tendencia)}
											/>
										</Tooltip>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	);
};

export default SlaPorCliente;
